/**
 * DV-UX-002: One-click status report copy hook
 *
 * Shared hook for building LLM-ready status reports from in-memory state.
 * No network calls, sensitive data redacted, size-capped output.
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useServerAuth } from '@/hooks/useAuth-cookie';
import { API_ENV } from '@/lib/api-base';
import {
  redactUserId,
  formatDuration,
  redactLogEntry,
  redactApiCall,
  redactStageState
} from '@/lib/redaction';
import { formatTimestamp } from '@/lib/format-utils';

// Size cap for reports (8KB)
const MAX_REPORT_SIZE = 8 * 1024;

interface StatusReportContext {
  pageId: string;
  webSocketState?: any;
  recentLogs?: any[];
  recentApiCalls?: any[];
  customSections?: Record<string, any>;
}

export function useStatusReport() {
  const queryClient = useQueryClient();
  const { user, isAuthenticated, isAdmin } = useServerAuth();

  const buildReport = useCallback((context: StatusReportContext): string => {
    const { pageId, webSocketState, recentLogs = [], recentApiCalls = [], customSections = {} } = context;

    try {
      const now = new Date();
      const correlationId = `status-${Date.now()}`;

      // Get redaction flag status synchronously (primarily from env)
      const redactionEnabled = import.meta.env.VITE_REDACTION_ENABLED === 'true';

      // Environment section
      const wsBase = import.meta.env.VITE_WS_URL || API_ENV.baseUrl;
      const environment = {
        api_base: API_ENV.baseUrl,
        ws_base: wsBase.replace(/^https?:/, 'wss:'), // Force wss:// scheme for WebSocket
        app_version: import.meta.env.VITE_APP_VERSION || 'dev',
        build_time: import.meta.env.VITE_BUILD_TIME || 'unknown',
        environment: API_ENV.isDevelopment ? 'development' : 'production',
        // Omit user_agent for now as it's not consistently useful
      };

      // Auth section
      const auth = {
        authenticated: isAuthenticated,
        user_id: user?.id ? redactUserId(user.id) : '[ANONYMOUS]',
        admin_flag: isAdmin,
        session_valid: !!user,
      };

      // Enhanced Query Client section with stage samples debugging
      const queryCache = queryClient.getQueryCache();
      const queries = queryCache.getAll();
      const staleQueries = queries.filter(q => q.isStale());

      // Find samples-related queries specifically
      const samplesQueries = queries.filter(q => {
        const keyStr = JSON.stringify(q.queryKey).toLowerCase();
        return keyStr.includes('sample') || keyStr.includes('stage');
      });

      const reactQuery = {
        total_queries: queries.length,
        stale_queries: staleQueries.length,
        samples_queries_count: samplesQueries.length,
        samples_queries_detail: samplesQueries.map(q => ({
          key: JSON.stringify(q.queryKey),
          status: q.state.status,
          data_available: !!q.state.data,
          error: q.state.error?.message || null,
          last_fetch: q.state.dataUpdatedAt > 0 ? formatTimestamp(q.state.dataUpdatedAt) : 'never',
          fetch_count: q.state.fetchStatus,
          is_enabled: (q.options as any)?.enabled !== false,
          is_stale: q.isStale(),
        })),
        recent_fetches: queries
          .filter(q => q.state.dataUpdatedAt > 0)
          .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)
          .slice(0, 5)
          .map(q => ({
            key: JSON.stringify(q.queryKey).slice(0, 50),
            age: formatDuration(now.getTime() - q.state.dataUpdatedAt),
            status: q.state.status,
          })),
        query_client_config: {
          default_stale_time: queryClient.getDefaultOptions().queries?.staleTime || 'unknown',
          default_cache_time: queryClient.getDefaultOptions().queries?.gcTime || 'unknown',
          default_retry: queryClient.getDefaultOptions().queries?.retry || 'unknown',
          default_refetch_on_window_focus: queryClient.getDefaultOptions().queries?.refetchOnWindowFocus || 'unknown',
        },
      };

      // Build main sections
      const sections = [
        '# Demo Viewer Status Report',
        `**Page:** ${pageId} | **Generated:** ${now.toISOString()} | **ID:** ${correlationId}`,
        '',
        '## Environment',
        '```yaml',
        Object.entries(environment)
          .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
          .join('\n'),
        '```',
        '',
        '## Authentication',
        '```yaml',
        Object.entries(auth)
          .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
          .join('\n'),
        '```',
        '',
        '## Redaction Status',
        '```yaml',
        `redaction_enabled: ${JSON.stringify(redactionEnabled)}`,
        `feature_flag_source: ${redactionEnabled ? 'enabled' : 'default_disabled'}`,
        `transport_security: "always_enabled"`,
        `pii_redaction: ${redactionEnabled ? '"content_and_pii"' : '"secrets_only"'}`,
        '```',
        '',
        '## React Query Cache',
        '```yaml',
        Object.entries(reactQuery)
          .map(([key, value]) => `${key}: ${JSON.stringify(value, null, 2)}`)
          .join('\n'),
        '```',
        '',
        '## Hook Execution Diagnosis',
        '```yaml',
        `current_page: ${JSON.stringify(pageId)}`,
        `samples_queries_executing: ${JSON.stringify(samplesQueries.length > 0)}`,
        `stage_tester_route_active: ${JSON.stringify(pageId === 'stage-tester')}`,
        `recent_component_logs: ${JSON.stringify(recentLogs.filter(log =>
          log.text.includes('useStageTester') ||
          log.text.includes('StageCard') ||
          log.text.includes('samples')
        ).slice(-3).map(log => ({
          timestamp: formatTimestamp(log.ts),
          message: log.text.slice(0, 80)
        })))}`,
        '```',
      ];

      // Add page-specific sections
      if (pageId === 'websockets' && webSocketState) {
        sections.push(
          '',
          '## WebSocket State',
          '```yaml',
          `status: ${JSON.stringify(webSocketState.status)}`,
          `is_polling_fallback: ${JSON.stringify(webSocketState.isPollingFallback)}`,
          `last_update: ${webSocketState.lastUpdate ? formatTimestamp(webSocketState.lastUpdate) : 'none'}`,
          `error: ${webSocketState.error ? JSON.stringify(webSocketState.error.message) : 'none'}`,
          '```'
        );
      }

      // Add troubleshooting section for API issues
      const apiCalls = recentApiCalls || [];
      const authErrors = apiCalls.filter(call => call.meta?.status === 401 || call.meta?.status === 403);
      if (authErrors.length > 0) {
        sections.push(
          '',
          '## API Troubleshooting',
          '```yaml',
          `auth_errors_detected: ${authErrors.length}`,
          `user_has_admin_role: ${auth.admin_flag}`,
          `demo_endpoints_require_admin: true`,
          `suggested_solutions:`,
          `  - "Contact admin to grant admin role"`,
          `  - "Check if user should have access to demo endpoints"`,
          `  - "Verify authentication token is valid"`,
          `common_endpoints_failing:`,
          ...authErrors.slice(0, 3).map(call => {
            const method = call.meta?.method || 'UNKNOWN';
            const path = call.meta?.path || call.meta?.url || 'unknown-endpoint';
            return `  - "${method} ${path}" (${call.meta?.status})`;
          }),
          '```'
        );
      }

      // Add custom sections
      Object.entries(customSections).forEach(([sectionName, data]) => {
        sections.push(
          '',
          `## ${sectionName}`,
          '```yaml',
          Object.entries(data)
            .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
            .join('\n'),
          '```'
        );
      });

      // Recent API calls section with enhanced error details
      if (recentApiCalls.length > 0) {
        const failedCalls = recentApiCalls.filter(call => call.meta?.status >= 400);
        const successCalls = recentApiCalls.filter(call => call.meta?.status < 400);

        sections.push(
          '',
          '## Recent API Calls',
          `**Summary:** ${successCalls.length} successful, ${failedCalls.length} failed`,
          ''
        );

        // Show failed calls first for debugging
        if (failedCalls.length > 0) {
          sections.push(
            '**Failed Calls:**',
            ...failedCalls.slice(0, 5).map(call => {
              const errorDetails = call.meta?.error ? ` - ${call.meta.error}` : '';
              const authNote = call.meta?.status === 401 || call.meta?.status === 403 ? ' ⚠️ AUTH ISSUE' : '';
              const method = call.meta?.method || 'UNKNOWN';
              const path = call.meta?.path || call.meta?.url || 'unknown-endpoint';
              const elapsed = call.meta?.elapsed || call.meta?.duration || 0;
              return `- **${method} ${path}** → ${call.meta?.status}${authNote} (${elapsed}ms)${errorDetails} [${call.meta?.correlationId || 'no-id'}]`;
            }),
            ''
          );
        }

        // Show successful calls
        if (successCalls.length > 0) {
          sections.push(
            '**Successful Calls:**',
            ...successCalls.slice(0, 3).map(call => {
              const method = call.meta?.method || 'UNKNOWN';
              const path = call.meta?.path || call.meta?.url || 'unknown-endpoint';
              const elapsed = call.meta?.elapsed || call.meta?.duration || 0;
              return `- **${method} ${path}** → ${call.meta?.status} (${elapsed}ms) [${call.meta?.correlationId || 'no-id'}]`;
            })
          );
        }
      }

      // Recent logs section
      if (recentLogs.length > 0) {
        sections.push(
          '',
          '## Recent Logs',
          ...recentLogs.slice(-20).map(log =>
            `- **${log.level.toUpperCase()}** [${formatTimestamp(log.ts)}] ${log.text.slice(0, 300)}`
          )
        );
      }

      sections.push(
        '',
        '## Notes',
        `- Content redaction: ${redactionEnabled ? 'ENABLED (content + secrets)' : 'DISABLED (secrets only)'}`,
        '- Transport-level security: Always enabled (API keys, tokens always masked)',
        '- Report built from in-memory state only (no network calls)',
        '- Timestamps relative to report generation time',
        `- Report size: ${sections.join('\n').length} bytes (max: ${MAX_REPORT_SIZE} bytes)`,
        '- Enhanced with variable analysis and source details',
        `- Generated: ${now.toISOString()} | ID: ${correlationId}`
      );

      let report = sections.join('\n');

      // Enhanced truncation with size markers
      if (report.length > MAX_REPORT_SIZE) {
        const originalSize = report.length;
        const truncateMarker = `\n\n---\n**REPORT TRUNCATED**\n- Original size: ${Math.round(originalSize / 1024 * 100) / 100}KB\n- Truncated to: ${Math.round(MAX_REPORT_SIZE / 1024 * 100) / 100}KB\n- Excess removed: ${Math.round((originalSize - MAX_REPORT_SIZE) / 1024 * 100) / 100}KB\n- Use redaction or filter specific sections for full report\n---`;

        const maxContentSize = MAX_REPORT_SIZE - truncateMarker.length;
        report = report.slice(0, maxContentSize) + truncateMarker;
      }

      return report;

    } catch (error) {
      return `# Status Report Error

**Page:** ${pageId}
**Generated:** ${new Date().toISOString()}

## Error
Failed to generate status report: ${error instanceof Error ? error.message : 'Unknown error'}

## Minimal Info
- Environment: ${API_ENV.isDevelopment ? 'development' : 'production'}
- API Base: ${API_ENV.baseUrl}
- Authenticated: ${isAuthenticated}

Please check console for detailed error information.`;
    }
  }, [queryClient, user, isAuthenticated]);

  const buildWebSocketReport = useCallback((
    webSocketState: any,
    recentLogs: any[] = [],
    recentApiCalls: any[] = [],
    events: any[] = [],
    executionSelectorData?: {
      selectedExecutionId: string | null;
      availableExecutions: any[];
      lastFetchStatus: 'success' | 'error' | 'loading';
      lastFetchError?: string;
      lastUpdatedAt?: string;
    }
  ): string => {
    const eventStats = events.reduce((acc, event) => {
      acc[event.event_type] = (acc[event.event_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const customSections: Record<string, any> = {
      'WebSocket Events': {
        total_events: events.length,
        events_by_type: eventStats,
        recent_events: events.slice(-5).map(e => ({
          type: e.event_type,
          stage: e.stage,
          timestamp: formatTimestamp(e.timestamp),
          execution_id: e.execution_id || 'none',
        })),
      },
    };

    // Add execution selector data if provided
    if (executionSelectorData) {
      customSections['Execution Selector'] = {
        selected_execution_id: executionSelectorData.selectedExecutionId || 'none',
        available_count: executionSelectorData.availableExecutions.length,
        last_fetch_status: executionSelectorData.lastFetchStatus,
        last_fetch_error: executionSelectorData.lastFetchError || 'none',
        last_updated_at: executionSelectorData.lastUpdatedAt ?
          formatTimestamp(executionSelectorData.lastUpdatedAt) : 'none',
      };
    }

    // Add enhanced WebSocket connection details if available
    if (webSocketState) {
      customSections['WebSocket Connection'] = {
        transport: webSocketState.transport || 'websocket',
        reconnect_attempts: webSocketState.reconnectAttempts || 0,
        backoff_ms: webSocketState.backoffMs || 1000,
        last_connect_at: webSocketState.lastConnectAt ?
          formatTimestamp(webSocketState.lastConnectAt) : 'none',
        last_disconnect_at: webSocketState.lastDisconnectAt ?
          formatTimestamp(webSocketState.lastDisconnectAt) : 'none',
        ping_interval_ms: webSocketState.pingIntervalMs || 25000,
        last_ping_latency_ms: webSocketState.lastPingLatencyMs || 'none',
        joined_rooms: webSocketState.joinedRooms || [],
        join_ack_status: webSocketState.joinAckStatus || 'none',
        auto_join_user_room: webSocketState.autoJoinUserRoom ?? true,
      };
    }

    return buildReport({
      pageId: 'websockets',
      webSocketState,
      recentLogs: recentLogs.filter(log =>
        log.meta?.source === 'websocket' ||
        log.text.toLowerCase().includes('websocket')
      ),
      recentApiCalls,
      customSections,
    });
  }, [buildReport]);

  const buildTemporalReport = useCallback((
    executionId: string,
    filters: any,
    events: any[] = [],
    recentLogs: any[] = [],
    recentApiCalls: any[] = [],
    isLiveMode: boolean = false,
    pollingInterval: number = 3000,
    pagination: { totalEvents: number; loadedCount: number; hasNextPage: boolean } = {
      totalEvents: 0,
      loadedCount: 0,
      hasNextPage: false
    },
    deduplicationStats?: any,
    temporalMetadata?: {
      workflowId?: string;
      runId?: string;
      workflowLinks?: any;
    },
    timingMetrics?: {
      stageTimings?: Record<string, { startTime?: string; endTime?: string; durationMs?: number }>;
      pipelineStartTime?: string;
      serverClockSkew?: number;
    }
  ): string => {
    const customSections = {
      'Selection': {
        execution_id: executionId || 'none', // Full execution ID for debugging
        execution_id_format: executionId ? (executionId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i) ? 'valid_uuid' : 'invalid_format') : 'missing',
        execution_id_length: executionId?.length || 0,
        selected_filters: Object.keys(filters).length,
        filter_details: {
          stages: filters.stage?.length || 0,
          event_types: filters.eventType?.length || 0,
          personas: filters.persona?.length || 0,
          time_range: !!(filters.timeRange?.from && filters.timeRange?.to)
        }
      },
      'Temporal Workflow': temporalMetadata ? {
        workflow_id: temporalMetadata.workflowId || 'none',
        run_id: temporalMetadata.runId?.slice(-8) || 'none',
        workflow_links_available: !!temporalMetadata.workflowLinks,
        temporal_web_urls: temporalMetadata.workflowLinks ? {
          workflow: temporalMetadata.workflowLinks.workflow ? 'available' : 'none',
          history: temporalMetadata.workflowLinks.history ? 'available' : 'none',
          summary: temporalMetadata.workflowLinks.summary ? 'available' : 'none',
          stack_trace: temporalMetadata.workflowLinks.stackTrace ? 'available' : 'none'
        } : 'none'
      } : {
        status: 'no_temporal_metadata',
        message: 'Temporal workflow identifiers not available'
      },
      'Timing Metrics': timingMetrics ? {
        pipeline_start: timingMetrics.pipelineStartTime ? formatTimestamp(timingMetrics.pipelineStartTime) : 'none',
        server_clock_skew_ms: timingMetrics.serverClockSkew || 0,
        clock_skew_status: timingMetrics.serverClockSkew ?
          (Math.abs(timingMetrics.serverClockSkew) > 5000 ? 'significant_skew' : 'minor_skew') : 'unknown',
        stage_timings_available: !!timingMetrics.stageTimings,
        stage_summary: timingMetrics.stageTimings ? Object.entries(timingMetrics.stageTimings).map(([stage, timing]) => ({
          stage,
          duration_ms: timing.durationMs || 0,
          duration_formatted: timing.durationMs ? formatDuration(timing.durationMs) : 'unknown',
          start_time: timing.startTime ? formatTimestamp(timing.startTime) : 'none',
          end_time: timing.endTime ? formatTimestamp(timing.endTime) : 'none',
          status: timing.endTime ? 'completed' : 'in_progress'
        })).slice(0, 10) : [] // Limit to prevent bloat
      } : {
        status: 'no_timing_data',
        message: 'Stage timing metrics not available'
      },
      'Pagination': {
        total_events: pagination.totalEvents,
        loaded_count: pagination.loadedCount,
        has_next_page: pagination.hasNextPage,
        loading_percentage: pagination.totalEvents > 0 ?
          Math.round((pagination.loadedCount / pagination.totalEvents) * 100) : 0
      },
      'Event Deduplication': {
        raw_events_received: deduplicationStats?.total || 0,
        duplicates_removed: deduplicationStats?.duplicatesRemoved || 0,
        final_events_count: deduplicationStats?.finalCount || 0,
        deduplication_rate: deduplicationStats?.deduplicationRate ?
          `${(deduplicationStats.deduplicationRate * 100).toFixed(1)}%` : '0%',
        methods_used: {
          by_event_id: deduplicationStats?.deduplicationMethods?.by_id || 0,
          by_correlation_id: deduplicationStats?.deduplicationMethods?.by_correlation_id || 0
        },
        effectiveness: deduplicationStats?.duplicatesRemoved > 0 ? 'active' : 'minimal'
      },
      'Live Mode': {
        enabled: isLiveMode,
        polling_interval_ms: pollingInterval,
        mode_description: isLiveMode ? 'WebSocket hints + fast polling' : 'standard polling',
        last_ws_hint: 'none', // TODO: Add WebSocket hint tracking
      },
      'Request Correlation': {
        recent_api_calls: recentApiCalls.length,
        last_request_id: recentApiCalls[0]?.meta?.requestId?.slice(-8) || 'none',
        last_response_id: recentApiCalls[0]?.meta?.responseId?.slice(-8) || 'none',
        correlation_match: recentApiCalls[0]?.meta?.requestId && recentApiCalls[0]?.meta?.responseId ?
          'server_supports_correlation' : 'no_server_correlation',
        api_call_timeline: recentApiCalls.slice(0, 3).map(call => ({
          timestamp: call.ts,
          method: call.meta?.method,
          status: call.meta?.status,
          request_id: call.meta?.requestId?.slice(-8)
        }))
      },
      'Recent Events': {
        event_count: events.length,
        event_types: events.reduce((acc, event) => {
          acc[event.event_type] = (acc[event.event_type] || 0) + 1;
          return acc;
        }, {} as Record<string, number>),
        recent_sample: events.slice(-3).map(e => ({
          type: e.event_type,
          stage: e.stage,
          timestamp: formatTimestamp(e.timestamp),
          severity: e.severity || 'info'
        }))
      },
      'API Debugging': {
        expected_endpoint: `/api/demo/temporal/events?execution_id=${executionId}&page=1`,
        total_events_from_api: events.length,
        events_loaded_successfully: events.length > 0,
        api_query_in_cache: recentApiCalls.some(call =>
          call.meta?.path?.includes('/api/demo/temporal/events') &&
          call.meta?.path?.includes(executionId || '')
        ),
        last_events_api_call: recentApiCalls
          .filter(call => call.meta?.path?.includes('temporal/events'))
          .slice(0, 1)
          .map(call => ({
            timestamp: formatTimestamp(call.ts),
            status: call.meta?.status || 'unknown',
            path: call.meta?.path || 'unknown',
            response_size: call.meta?.response_size || 'unknown',
            execution_id_in_path: call.meta?.path?.includes(executionId || '') || false
          }))[0] || 'no_temporal_events_calls_found'
      }
    };

    return buildReport({
      pageId: 'temporal-events',
      recentLogs: recentLogs.filter(log =>
        log.meta?.source === 'temporal' ||
        log.text.toLowerCase().includes('temporal') ||
        log.text.toLowerCase().includes('event')
      ),
      recentApiCalls,
      customSections,
    });
  }, [buildReport]);

  const buildHeartbeatsReport = useCallback((
    executionId: string,
    selectedStage: string,
    heartbeats: any[] = [],
    qcSummary: any = null,
    recentLogs: any[] = [],
    recentApiCalls: any[] = [],
    isLiveMode: boolean = false,
    pollingIntervals: { heartbeats: number; qc: number } = { heartbeats: 5000, qc: 30000 },
    heartbeatsError?: any,
    qcError?: any
  ): string => {
    const activeHeartbeats = heartbeats.filter(hb => hb.isActive);
    const stageHeartbeat = heartbeats.find(hb => hb.stage === selectedStage);

    const customSections = {
      'Selection': {
        execution_id: executionId || 'none', // Full execution ID for debugging
        selected_stage: selectedStage || 'none',
      },
      'Heartbeats Status': {
        total_stages: heartbeats.length,
        active_stages: activeHeartbeats.length,
        inactive_stages: heartbeats.length - activeHeartbeats.length,
        selected_stage_active: stageHeartbeat?.isActive || false,
        selected_stage_progress: stageHeartbeat?.progress || 0,
        selected_stage_iteration: stageHeartbeat?.iteration || 0,
        selected_stage_message: stageHeartbeat?.message || 'none',
        selected_stage_last_heartbeat: stageHeartbeat?.lastHeartbeat ?
          formatTimestamp(stageHeartbeat.lastHeartbeat) : 'none',
        stage_summary: heartbeats.map(hb => ({
          stage: hb.stage,
          active: hb.isActive,
          progress: hb.progress,
          iteration: hb.iteration,
          last_beat: hb.lastHeartbeat ? formatTimestamp(hb.lastHeartbeat) : 'none'
        }))
      },
      'QC Summary': qcSummary ? {
        persona_slug: qcSummary.personaSlug || 'none',
        rubric_hash: qcSummary.rubricHash?.slice(-8) || 'none', // Last 8 chars only
        weighted_score: qcSummary.weightedScore || 0,
        decision: qcSummary.decision || 'none',
        thresholds: qcSummary.thresholds || {},
        scores: qcSummary.scores ? Object.keys(qcSummary.scores).length : 0,
      } : {
        status: qcError ? 'api_error' : 'no_qc_data',
        message: qcError ? `QC API Error: ${qcError.status} - ${qcError.message}` : 'QC summary not available for selected stage',
        error_details: qcError ? {
          status_code: qcError.status,
          error_message: qcError.message,
          is_auth_error: qcError.status === 401 || qcError.status === 403,
          suggested_action: qcError.status === 403 ? 'User needs admin access for demo endpoints' : 'Check authentication'
        } : null
      },
      'Heartbeats API Status': heartbeatsError ? {
        status: 'api_error',
        error_code: heartbeatsError.status,
        error_message: heartbeatsError.message,
        is_auth_error: heartbeatsError.status === 401 || heartbeatsError.status === 403,
        suggested_action: heartbeatsError.status === 403 ?
          'User needs admin access for demo endpoints' :
          heartbeatsError.status === 401 ? 'Authentication required' : 'Check API connectivity',
        api_endpoint: '/api/demo/temporal/heartbeats',
        last_error_time: formatTimestamp(Date.now()),
      } : {
        status: heartbeats.length > 0 ? 'success' : 'no_data',
        message: heartbeats.length > 0 ? 'Heartbeats data loaded successfully' : 'No heartbeats data available'
      },
      'Live Mode': {
        enabled: isLiveMode,
        heartbeats_polling_ms: pollingIntervals.heartbeats,
        qc_polling_ms: pollingIntervals.qc,
        mode_description: isLiveMode ?
          'fast polling with jitter' : 'standard polling',
        jitter_applied: isLiveMode ? 'yes (100-600ms)' : 'no'
      }
    };

    return buildReport({
      pageId: 'heartbeats',
      recentLogs: recentLogs.filter(log =>
        log.meta?.source === 'heartbeats' ||
        log.text.toLowerCase().includes('heartbeat') ||
        log.text.toLowerCase().includes('qc') ||
        log.text.toLowerCase().includes('stage')
      ),
      recentApiCalls,
      customSections,
    });
  }, [buildReport]);

  const buildStageOutputsReport = useCallback((
    stageData: any,
    verifications: Record<string, any> = {},
    recentLogs: any[] = [],
    recentApiCalls: any[] = [],
    executionId?: string
  ): string => {
    const totalOutputs = stageData?.total_outputs || 0;
    const stagesList = stageData?.stages || [];
    const outputsByStage = stageData?.outputs_by_stage || {};

    // Enhanced execution ID and data analysis
    const hasValidData = stagesList.length > 0 && totalOutputs > 0;
    const querySuccessful = recentApiCalls.some(call =>
      call.meta?.path?.includes('storage/outputs') && call.meta?.status === 200
    );

    // Verification stats
    const verificationUrls = Object.keys(verifications);
    const successfulVerifications = Object.values(verifications).filter((v: any) => v.status === 'success').length;
    const failedVerifications = Object.values(verifications).filter((v: any) => v.status === 'failed').length;
    const corsErrors = Object.values(verifications).filter((v: any) => v.status === 'cors-error').length;
    const networkErrors = Object.values(verifications).filter((v: any) => v.status === 'network-error').length;
    const mixedContentErrors = Object.values(verifications).filter((v: any) => v.status === 'mixed-content').length;

    // Domain insights using the existing groupVerificationsByHost function
    const hostStats = verificationUrls.length > 0 ?
      // We need to import this function - for now use placeholder logic
      Object.entries(verifications).reduce((acc, [url, verification]: [string, any]) => {
        let hostname: string;
        try {
          hostname = new URL(url).hostname;
        } catch {
          hostname = 'invalid-url';
        }

        if (!acc[hostname]) {
          acc[hostname] = { total: 0, success: 0, failed: 0, cors: 0, network: 0 };
        }

        acc[hostname].total++;
        if (verification.status === 'success') acc[hostname].success++;
        else if (verification.status === 'cors-error') acc[hostname].cors++;
        else if (verification.status === 'network-error') acc[hostname].network++;
        else acc[hostname].failed++;

        return acc;
      }, {} as Record<string, any>) : {};

    // URL extraction summary
    let totalUrls = 0;
    const artifactStats: Record<string, number> = {};

    stagesList.forEach((stage: string) => {
      const outputs = outputsByStage[stage] || [];
      outputs.forEach((output: any) => {
        // Count URL artifacts
        if (output.metadata?.storage_url) totalUrls++;

        // Classify content types
        if (output.content) {
          Object.entries(output.content).forEach(([_, value]) => {
            if (typeof value === 'string' && (value.startsWith('http') || value.startsWith('https'))) {
              totalUrls++;
              artifactStats.urls = (artifactStats.urls || 0) + 1;
            } else if (typeof value === 'object') {
              artifactStats.json = (artifactStats.json || 0) + 1;
            } else if (typeof value === 'string') {
              artifactStats.text = (artifactStats.text || 0) + 1;
            }
          });
        }
      });
    });

    const customSections = {
      'Stage Outputs': {
        execution_id: executionId || 'none',
        total_outputs: totalOutputs,
        total_stages: stagesList.length,
        total_urls_found: totalUrls,
        artifact_breakdown: artifactStats,
        stages_summary: stagesList.map((stage: string) => ({
          stage,
          output_count: outputsByStage[stage]?.length || 0
        })).slice(0, 5) // Limit to prevent bloat
      },
      'Execution Status Analysis': {
        execution_provided: !!executionId,
        execution_id_length: executionId?.length || 0,
        execution_id_format_valid: executionId?.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i) ? true : false,
        query_successful: querySuccessful,
        api_returned_data: hasValidData,
        outputs_found: totalOutputs > 0,
        stages_found: stagesList.length > 0,
        diagnosis: !executionId ? 'no_execution_id' :
                  !querySuccessful ? 'api_failure' :
                  !hasValidData ? 'empty_response_data' :
                  !totalOutputs ? 'execution_has_no_outputs' : 'data_available',
        possible_issues: !hasValidData ? [
          'Execution ID does not exist in database',
          'Pipeline stages not creating outputs',
          'RLS policies blocking data access',
          'Execution exists but no stages have completed',
          'Demo viewer showing wrong/hardcoded execution ID'
        ] : 'none',
        suggested_actions: !hasValidData ? [
          'Check if execution ID is valid',
          'Verify recent executions have stage outputs',
          'Test with known working execution ID',
          'Check database for recent stage_outputs entries',
          'Use execution selector to pick valid execution with outputs'
        ] : 'none'
      },
      'Database State Hints': {
        last_stage_outputs_api_call: recentApiCalls
          .filter(call => call.meta?.path?.includes('storage/outputs'))
          .slice(0, 1)
          .map(call => ({
            timestamp: formatTimestamp(call.ts),
            status: call.meta?.status || 'unknown',
            path: call.meta?.path || 'unknown',
            execution_id_queried: call.meta?.path?.split('=')[1]?.slice(0, 12) || 'unknown',
            response_size: call.meta?.response_size || 'unknown',
            total_outputs_returned: call.meta?.total_outputs || 'unknown'
          }))[0] || 'no_recent_calls',
        cache_hit_detected: recentApiCalls
          .filter(call => call.meta?.path?.includes('storage/outputs'))
          .some(call => call.meta?.from_cache === true),
        cache_status: 'check_react_query_cache_section'
      },
      'URL Verification': {
        total_verified: verificationUrls.length,
        successful: successfulVerifications,
        failed: failedVerifications,
        cors_errors: corsErrors,
        network_errors: networkErrors,
        mixed_content_errors: mixedContentErrors,
        success_rate: verificationUrls.length > 0 ?
          Math.round((successfulVerifications / verificationUrls.length) * 100) : 0,
        sample_verifications: Object.entries(verifications).slice(0, 3).map(([url, result]: [string, any]) => ({
          url: url.slice(0, 50) + (url.length > 50 ? '...' : ''),
          status: result.status,
          response_time: result.responseTime ? `${result.responseTime}ms` : 'unknown',
          method: result.headers?.['x-verification-method'] || 'unknown'
        }))
      },
      'Domain Breakdown': Object.keys(hostStats).length > 0 ? {
        total_domains: Object.keys(hostStats).length,
        domain_summary: Object.entries(hostStats).slice(0, 5).map(([hostname, stats]: [string, any]) => ({
          hostname: hostname.slice(0, 30) + (hostname.length > 30 ? '...' : ''),
          total_urls: stats.total,
          success: stats.success,
          cors_errors: stats.cors,
          network_errors: stats.network,
          other_failures: stats.failed,
          success_rate: Math.round((stats.success / stats.total) * 100)
        }))
      } : {
        status: 'no_domains',
        message: 'No URL verifications performed yet'
      }
    };

    return buildReport({
      pageId: 'stage-outputs',
      recentLogs,
      recentApiCalls,
      customSections
    });
  }, [buildReport]);

  const buildFeatureFlagsReport = useCallback((
    flags: any[] = [],
    recentLogs: any[] = [],
    recentApiCalls: any[] = [],
    lastUpdate?: {
      flagName: string;
      fieldsChanged: string[];
      timestamp: string;
    }
  ): string => {
    const enabledFlags = flags.filter(flag => flag.effective_status?.enabled);
    const adminFlags = flags.filter(flag => flag.admin_enabled);

    const customSections = {
      'Feature Flags': {
        total_flags: flags.length,
        enabled_count: enabledFlags.length,
        admin_enabled_count: adminFlags.length,
        enabled_percentage: flags.length > 0 ? Math.round((enabledFlags.length / flags.length) * 100) : 0,
        sample_flags: flags.slice(0, 5).map(flag => ({
          name: flag.name,
          enabled: flag.enabled || false,
          admin_enabled: flag.admin_enabled || false,
          rollout_percentage: flag.rollout_percentage || 0,
          effective_enabled: flag.effective_status?.enabled || false,
          effective_reason: flag.effective_status?.reason || 'unknown'
        }))
      },
      'Last Update': lastUpdate ? {
        flag_name: lastUpdate.flagName,
        fields_changed: lastUpdate.fieldsChanged,
        updated_at: formatTimestamp(lastUpdate.timestamp),
        correlation_id: `ff-update-${Date.now()}`
      } : {
        status: 'no_recent_updates',
        message: 'No recent flag updates detected'
      }
    };

    return buildReport({
      pageId: 'feature-flags',
      recentLogs: recentLogs.filter(log =>
        log.meta?.source === 'feature_flags' ||
        log.text.toLowerCase().includes('feature') ||
        log.text.toLowerCase().includes('flag')
      ),
      recentApiCalls,
      customSections,
    });
  }, [buildReport]);

  const buildStageTesterReport = useCallback((
    stageStates: Record<string, any>,
    chainMode: boolean = false,
    recentLogs: any[] = [],
    recentApiCalls: any[] = [],
    additionalContext?: {
      temporalState?: any;
      databaseErrors?: any[];
      backendStatus?: any;
      environmentConfig?: any;
    }
  ): string => {
    // Use central redaction for stage states
    const redactedStates = Object.fromEntries(
      Object.entries(stageStates).map(([stageId, state]) => [stageId, redactStageState(state)])
    );

    // Enhanced stage analysis with variable details
    const activeStages = Object.entries(redactedStates)
      .filter(([_, state]) => state.expanded || state.isRunning || state.lastRun)
      .map(([stageId, state]) => {
        // Analyze prompt variables if available
        const promptVars = state.promptPreview?.variables || {};
        const promptVariableCount = Object.keys(promptVars).length;
        const missingVariables = Object.entries(promptVars)
          .filter(([_, value]) => value === undefined || value === null || value === '')
          .map(([key, _]) => key);

        return {
          stage: stageId,
          // Enhanced source details
          input_source_type: state.inputSource || 'unknown',
          source_details: {
            sample_id: state.selectedSampleId || 'none',
            custom_input_size: state.customInput ? `${state.customInput.length}b` : 'none',
            last_output_origin: state.lastOutput?.origin || 'none',
            effective_input_available: !!state.effectiveInput
          },
          // Enhanced prompt analysis
          prompt_status: {
            hash: state.promptHash || 'none',
            variable_count: promptVariableCount,
            missing_variables: missingVariables.length > 0 ? missingVariables : 'none',
            variables_complete: missingVariables.length === 0,
            tokens_estimate: state.promptPreview?.tokensEstimate || 'unknown'
          },
          // Execution details
          execution_state: {
            validation_request_id: state.validationRequestId?.slice(-8) || 'none',
            sandbox_mode: state.sandboxMode,
            save_to_execution: state.saveToExecution,
            is_running: state.isRunning,
            has_overrides: !!state.overrides && Object.keys(state.overrides).length > 0
          },
          last_run: state.lastRun ? {
            success: state.lastRun.success,
            duration_ms: state.lastRun.duration,
            cost: state.lastRun.cost,
            tokens: state.lastRun.tokens,
            request_id: state.lastRun.requestId?.slice(-8)
          } : 'none',
        };
      });

    // Enhanced error analysis
    const executionErrors = recentApiCalls
      .filter(call => call.meta?.status >= 400)
      .filter(call => call.text.toLowerCase().includes('execution') || call.text.toLowerCase().includes('sandbox'))
      .slice(0, 5);

    const sampleErrors = recentLogs
      .filter(log => log.level === 'error' && log.text.toLowerCase().includes('sample'))
      .slice(0, 3);

    const databaseErrors = additionalContext?.databaseErrors ||
      recentLogs.filter(log =>
        log.level === 'error' &&
        (log.text.includes('permission denied') ||
         log.text.includes('pipeline_name') ||
         log.text.includes('RLS') ||
         log.text.includes('table'))
      ).slice(0, 3);

    // Sample loading status analysis - read directly from React Query cache
    const stageIds = ['script', 'vision', 'storyboard', 'frames', 'video', 'qc', 'export', 'music'];
    const sampleLoadingStages = stageIds.map(stageId => {
      const query = queryClient.getQueryCache().find({
        queryKey: ['demo', 'stage-tester', 'samples', stageId]
      });

      let samplesCount = 0;
      if (query?.state.data) {
        try {
          // Extract samples from React Query cache data
          const data = query.state.data as any;
          if (data?.data?.items && Array.isArray(data.data.items)) {
            samplesCount = data.data.items.length;
          }
        } catch (error) {
          // Fallback to 0 if data extraction fails
        }
      }

      return {
        stage: stageId,
        samples_loaded: samplesCount,
        samples_error: query?.state.error?.message || null,
        last_sample_fetch: query?.state.dataUpdatedAt && query.state.dataUpdatedAt > 0
          ? formatTimestamp(query.state.dataUpdatedAt)
          : 'never'
      };
    });

    const customSections = {
      'Stage Tester Configuration': {
        chain_mode_enabled: chainMode,
        total_stages: Object.keys(stageStates).length,
        active_stages: activeStages.length,
        running_stages: Object.values(stageStates).filter((s: any) => s.isRunning).length,
        sandbox_stages: Object.values(stageStates).filter((s: any) => s.sandboxMode).length,
        live_stages: Object.values(stageStates).filter((s: any) => !s.sandboxMode).length,
      },
      'Active Stages': activeStages.length > 0 ? activeStages : {
        status: 'no_active_stages',
        message: 'No stages have been configured or run yet'
      },
      'Recent Operations': {
        total_log_entries: recentLogs.length,
        errors_count: recentLogs.filter(l => l.level === 'error').length,
        warnings_count: recentLogs.filter(l => l.level === 'warn').length,
        recent_entries: recentLogs.slice(-5).map(l => ({
          timestamp: l.ts,
          level: l.level,
          message: l.text.slice(0, 100),
          stage: l.meta?.stage || 'general',
          correlation_id: l.meta?.correlationId?.slice(-8) || 'none',
        }))
      },
      'Input Source Statistics': {
        precanned_stages: Object.values(stageStates).filter((s: any) => s.inputSource === 'precanned').length,
        custom_stages: Object.values(stageStates).filter((s: any) => s.inputSource === 'custom').length,
        lastoutput_stages: Object.values(stageStates).filter((s: any) => s.inputSource === 'lastoutput').length,
        stages_with_samples: Object.values(stageStates).filter((s: any) => s.selectedSampleId).length,
        stages_with_custom_input: Object.values(stageStates).filter((s: any) => s.customInput && s.customInput.trim() !== '{\n  \n}').length,
      },
      'Sample Loading Analysis': {
        total_stages_checked: sampleLoadingStages.length,
        stages_with_samples: sampleLoadingStages.filter(s => s.samples_loaded > 0).length,
        stages_with_errors: sampleLoadingStages.filter(s => s.samples_error).length,
        sample_errors: sampleErrors.map(log => ({
          timestamp: log.ts,
          message: log.text.slice(0, 150),
          correlation_id: log.meta?.correlationId?.slice(-8) || 'none'
        })),
        per_stage_status: sampleLoadingStages.slice(0, 8), // Limit to prevent bloat
        // Debug React Query integration
        react_query_samples_queries: queryClient.getQueryCache().getAll()
          .filter(q => JSON.stringify(q.queryKey).toLowerCase().includes('sample'))
          .map(q => ({
            query_key: JSON.stringify(q.queryKey),
            state: q.state.status,
            enabled: (q.options as any)?.enabled !== false,
            data_exists: !!q.state.data,
            error_message: q.state.error?.message || null,
            last_update: q.state.dataUpdatedAt && q.state.dataUpdatedAt > 0
              ? formatTimestamp(q.state.dataUpdatedAt)
              : 'never'
          })),
        hook_status_diagnosis: {
          total_sample_queries_in_cache: queryClient.getQueryCache().getAll()
            .filter(q => JSON.stringify(q.queryKey).toLowerCase().includes('sample')).length,
          stage_tester_hooks_enabled: true, // Would need to pass this from component
          samples_api_endpoint_expected: '/api/demo/stage/{stage}/samples',
          query_client_type: queryClient.constructor.name,
          possible_issues: [
            'useStageTester hooks not mounting due to conditional rendering',
            'React Query queries disabled due to enabled: false condition',
            'API endpoints returning errors that prevent cache population',
            'Stage prop not provided to useStageTester hook',
            'Query client not properly configured for stage-tester routes'
          ]
        }
      },
      'Execution Error Analysis': {
        total_execution_errors: executionErrors.length,
        database_errors_detected: databaseErrors.length,
        recent_execution_failures: executionErrors.map(call => ({
          timestamp: call.ts,
          status: call.meta?.status,
          error: call.meta?.error || call.text.slice(0, 100),
          endpoint: call.meta?.path || 'unknown',
          correlation_id: call.meta?.correlationId?.slice(-8) || 'none'
        })),
        database_permission_issues: databaseErrors.map(log => ({
          timestamp: log.ts,
          message: log.text.slice(0, 200),
          type: log.text.includes('permission denied') ? 'permission_denied' :
                log.text.includes('pipeline_name') ? 'missing_column' :
                log.text.includes('RLS') ? 'rls_policy' : 'unknown'
        }))
      },
      'Backend Environment Status': additionalContext?.environmentConfig ? {
        simulation_mode: additionalContext.environmentConfig.useSimulation || 'unknown',
        skip_provider_calls: additionalContext.environmentConfig.skipProviderCalls || 'unknown',
        temporal_enabled: additionalContext.environmentConfig.temporalEnabled || 'unknown',
        backend_version: additionalContext.environmentConfig.version || 'unknown',
        database_connected: additionalContext.environmentConfig.databaseConnected || 'unknown'
      } : {
        status: 'no_backend_status',
        message: 'Backend environment configuration not available'
      },
      'Temporal Workflow State': additionalContext?.temporalState ? {
        workflow_available: !!additionalContext.temporalState.workflowId,
        workflow_id: additionalContext.temporalState.workflowId?.slice(-12) || 'none',
        workflow_status: additionalContext.temporalState.status || 'unknown',
        task_queue: additionalContext.temporalState.taskQueue || 'unknown',
        last_activity: additionalContext.temporalState.lastActivity ?
          formatTimestamp(additionalContext.temporalState.lastActivity) : 'none',
        error_count: additionalContext.temporalState.errors?.length || 0
      } : {
        status: 'no_temporal_data',
        message: 'Temporal workflow information not available'
      },
      'Component Mounting Diagnosis': {
        total_react_query_queries: queryClient.getQueryCache().getAll().length,
        stage_tester_page_active: 'stage-tester',
        api_calls_to_samples_endpoints: recentApiCalls.filter(call =>
          call.meta?.path?.includes('/samples') ||
          call.meta?.path?.includes('/stage/')
        ).length,
        recent_samples_api_calls: recentApiCalls
          .filter(call => call.meta?.path?.includes('/samples') || call.meta?.path?.includes('/stage/'))
          .slice(0, 3)
          .map(call => ({
            timestamp: formatTimestamp(call.ts),
            method: call.meta?.method || 'unknown',
            path: call.meta?.path || 'unknown',
            status: call.meta?.status || 'unknown',
            stage_in_path: call.meta?.path?.match(/\/stage\/([^\/]+)/)?.[1] || 'none'
          })),
        component_lifecycle_logs: recentLogs
          .filter(log =>
            log.text.includes('useStageTester') ||
            log.text.includes('StageCard') ||
            log.text.includes('mount') ||
            log.text.includes('unmount')
          )
          .slice(-5)
          .map(log => ({
            timestamp: formatTimestamp(log.ts),
            level: log.level,
            message: log.text.slice(0, 100),
            component: log.meta?.component || 'unknown'
          })),
        debugging_suggestions: [
          'Check browser dev tools React DevTools to see if StageCard components are mounting',
          'Look for useStageTester hook calls in browser console',
          'Verify stage prop is being passed to useStageTester hooks',
          'Check if API endpoints /api/demo/stage/{stage}/samples are being called',
          'Inspect network tab for failed requests to samples endpoints',
          'Check if QueryClientProvider is wrapping the stage-tester components'
        ]
      }
    };

    return buildReport({
      pageId: 'stage-tester',
      recentLogs: recentLogs
        .filter(log =>
          log.meta?.source === 'stage_tester' ||
          log.meta?.source === 'stage_card' ||
          log.meta?.source === 'input_source_picker' ||
          log.meta?.source === 'prompt_preview' ||
          log.meta?.source === 'run_controls' ||
          log.text.toLowerCase().includes('stage')
        )
        .map(entry => redactLogEntry(entry, false)),
      recentApiCalls: recentApiCalls.map(call => redactApiCall(call, false)),
      customSections,
    });
  }, [buildReport]);

  return {
    buildReport,
    buildWebSocketReport,
    buildTemporalReport,
    buildHeartbeatsReport,
    buildFeatureFlagsReport,
    buildStageOutputsReport,
    buildStageTesterReport,
  };
}